// The ONE rule that turns a corpus's `_assets/` glob into a lookup table, shared
// by the help corpus's diagrams (`stores/helpCorpusAssets.js`) and the
// collateral corpus's logos, icons and heroes (`stores/collateralCorpus.js`)
// (openspec/changes/artemis-proactive-onboarding, design D1 § Client).
//
// A FACTORY OVER THE CALLER'S GLOB, NOT A GLOB OF ITS OWN. Vite rewrites
// `import.meta.glob` at build time and only understands a LITERAL pattern
// written in the module that calls it, so no shared helper can glob on a
// corpus's behalf. Each corpus writes its own literal, eager glob over its own
// `_assets/` directory and hands the result here:
//
//   import.meta.glob("../../<corpus>/_assets/**/*", {
//     query: "?url&no-inline",
//     import: "default",
//     eager: true,
//   })
//
// `?url` makes every value the FINAL URL Vite serves the file at, hashed after
// a build; `import: "default"` makes it the bare string rather than a module
// namespace; `no-inline` keeps every asset a FILE rather than a `data:` URI
// (the reasoning lives in `stores/helpCorpusAssets.js`). This factory reads the
// values as they come; it cannot re-query a glob it did not write.

// The asset directory both server loaders skip (`::Corpus::Base::ASSET_DIRNAME`,
// app/services/corpus/base.rb). A key is the path BELOW it.
const ASSET_PATH_PATTERN = /(?:^|\/)_assets\/(.+)$/;

/**
 * `<path under _assets/> => <asset URL>` for one corpus.
 *
 * The key is the file's path relative to the corpus's `_assets/` directory,
 * because that is what the corpus's authors write: a help article's
 * `../_assets/<file>` and an `app/collateral/assets.yml` entry's
 * `light:`/`dark:` both name a file under `_assets/`. For a flat directory the
 * key is the filename. The glob keys themselves are relative to the CALLING
 * module, which no author ever sees.
 *
 * @param {Record<string, string>} modules - The caller's eager
 *   `?url&no-inline` glob result, `import: "default"`, keyed by module path.
 * @returns {Readonly<Record<string, string>>}
 * @throws {Error} when a glob key lies outside an `_assets/` directory -- a
 *   caller whose literal pattern reached past the corpus's asset directory.
 */
export function corpusAssetMap(modules) {
  return Object.freeze(
    Object.fromEntries(
      Object.keys(modules).map((path) => {
        const relative = path.match(ASSET_PATH_PATTERN)?.[1];
        if (!relative) {
          throw new Error(
            `corpusAssetMap: ${JSON.stringify(path)} is not under an _assets/ directory. ` +
              "Glob a corpus's _assets/ directory alone.",
          );
        }

        return [relative, modules[path]];
      }),
    ),
  );
}
